import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { MenuForm } from "@/components/staff/MenuForm";
import { BulkBar } from "@/components/staff/BulkBar";
import { api, errMsg, imgUrl, rupiah, CATEGORIES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { todayWIB } from "@/components/staff/attendance/utils";

const catLabel = (id) => CATEGORIES.find((c) => c.id === id)?.label || id;

export const MenuManager = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const { user } = useAuth();

  const load = useCallback(() => {
    api.get("/admin/menu").then((r) => {
      setItems(r.data);
      setSelected((s) => s.filter((id) => r.data.some((m) => m.id === id)));
    }).catch((e) => toast.error(errMsg(e)));
  }, []);

  const allOn = items.length > 0 && selected.length === items.length;
  const toggleAll = () => setSelected(allOn ? [] : items.map((m) => m.id));
  const toggleOne = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  useEffect(() => { load(); }, [load]);

  const save = async (f) => {
    const { id, ...body } = f;
    try {
      if (id) await api.put(`/admin/menu/${id}`, body);
      else await api.post("/admin/menu", body);
      toast.success("Menu disimpan");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Hapus ${m.name}?`)) return;
    await api.delete(`/admin/menu/${m.id}`).catch((e) => toast.error(errMsg(e)));
    load();
  };

  const toggle = (m, available) => save({ ...m, available });
  const approve = async (m) => {
    if (!window.confirm(`Setujui paket ${m.name} dan tampilkan di menu pelanggan?`)) return;
    try { await api.post(`/admin/menu/${m.id}/approve`); toast.success("Paket disetujui dan tampil di menu"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="font-serif font-semibold text-2xl">Daftar menu</h2>
        <button onClick={() => { setEditing(null); setOpen(true); }} className="btn-forest px-4 py-2.5 flex items-center gap-2 text-sm" data-testid="menu-add-button"><Plus className="w-4 h-4" /> Tambah menu</button>
      </div>
      {selected.length > 0 && <BulkBar ids={selected} onDone={load} onClear={() => setSelected([])} />}
      <div className="card-paper mt-5 divide-y divide-sage-200">
        <label className="flex items-center gap-4 px-4 py-3 bg-sage-50 rounded-t-2xl text-sm font-semibold cursor-pointer" data-testid="menu-select-all-row">
          <Checkbox checked={allOn ? true : selected.length ? "indeterminate" : false} onCheckedChange={toggleAll} data-testid="menu-select-all-checkbox" />
          Pilih semua <span className="font-normal text-muted2">({items.length} menu)</span>
        </label>
        {items.map((m) => (
          <div key={m.id} className={`flex ${m.pending_approval ? "flex-wrap sm:flex-nowrap" : ""} items-center gap-x-4 gap-y-2 p-4 ${selected.includes(m.id) ? "bg-sage-50" : ""}`} data-testid={`menu-row-${m.id}`}>
            <Checkbox checked={selected.includes(m.id)} onCheckedChange={() => toggleOne(m.id)} aria-label={`Pilih ${m.name}`} data-testid={`menu-select-checkbox-${m.id}`} />
            <img src={imgUrl(m.image)} alt="" className="w-16 h-16 rounded-lg object-cover bg-sage-100 shrink-0" />
            <div className={`flex-1 ${m.pending_approval ? "min-w-[120px]" : "min-w-0"}`}>
              <div className="font-semibold truncate">{m.name}</div>
              <div className="text-xs text-muted2">{catLabel(m.category)} · {rupiah(m.price)}</div>
              {m.category === "paket" && <div className="text-[11px] text-muted2" data-testid={`menu-promo-period-${m.id}`}>Promo: {m.promo_start || "–"} s/d {m.promo_end || "tanpa batas"}{m.promo_end && m.promo_end < todayWIB() && <b className="text-red-700"> · berakhir</b>}</div>}
              {m.pending_approval && <span className="inline-block mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700" data-testid={`menu-pending-badge-${m.id}`}>Menunggu persetujuan owner</span>}
            </div>
            {m.pending_approval && user?.role === "admin" && <button onClick={() => approve(m)} className="btn-forest px-3 py-1.5 text-xs order-last sm:order-none ml-auto sm:ml-0" data-testid={`menu-approve-button-${m.id}`}>Setujui</button>}
            <label className={`${m.pending_approval ? "hidden" : "hidden sm:flex"} items-center gap-2 text-xs text-muted2`}><Switch checked={m.available} onCheckedChange={(v) => toggle(m, v)} data-testid={`menu-available-switch-${m.id}`} /> {m.available ? "Tersedia" : "Habis"}</label>
            <button onClick={() => { setEditing(m); setOpen(true); }} className="p-2 rounded-lg hover:bg-sage-100" data-testid={`menu-edit-button-${m.id}`}><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(m)} className="p-2 rounded-lg hover:bg-red-50 text-red-700" data-testid={`menu-delete-button-${m.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <MenuForm open={open} item={editing} onClose={() => setOpen(false)} onSave={save} />
    </div>
  );
};
